import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { type HtmlHTMLAttributes, cloneElement, isValidElement, useCallback, useMemo, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

export type ModalBodyProps = {
  closeModal: () => void;
  modalRef: React.RefObject<HTMLDivElement | null>;
};

type Props = {
  title?: string;
  description?: string;
  button?: React.JSX.Element;
  className?: string;
  body?: (props: ModalBodyProps) => React.JSX.Element;
  onClose?: () => void;
  onOpen?: (() => void) | (() => Promise<void>);
  modalStyle?: React.CSSProperties;
  modalSize?: 'Small' | 'Medium' | 'Big';
  hideCloseButton?: boolean;
  buttonStyle?: React.CSSProperties;
  initOpen?: boolean;
  children?: React.ReactNode | ((props: ModalBodyProps) => React.ReactNode);
};

const ModalButton = ({ title, description, button, className, body, modalStyle, children, ...opts }: Props) => {
  const [open, setOpen] = useState(!button || opts?.initOpen || false);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleOpen = useCallback(async () => {
    await opts?.onOpen?.();
    setOpen(true);
  }, [opts?.onOpen, setOpen]);

  const handleClose = useCallback(() => {
    opts?.onClose?.();
    setOpen(false);
  }, [opts?.onClose, setOpen]);

  // pass close props on click
  const customButton = useMemo(() => {
    if (isValidElement(button)) {
      // @ts-ignore
      return cloneElement<HtmlHTMLAttributes<HTMLButtonElement>>(button as React.ReactElement, {
        onClick: handleOpen,
      });
    }
    return null;
  }, [button, handleOpen]);

  const Body = useMemo(() => {
    if (!!body) return () => body({ closeModal: handleClose, modalRef });
    if (!!children && typeof children === 'function') return () => children({ closeModal: handleClose, modalRef });
    if (!!children) return () => children;

    return () => <></>;
  }, [body, children, handleClose, modalRef]);

  return (
    <>
      {customButton ||
        (title && (
          <Button onClick={handleOpen} style={opts?.buttonStyle}>
            {title}
          </Button>
        ))}
      <Modal
        open={open}
        onClose={handleClose}
        className={className}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={`ZnglyModal ${opts?.modalSize ? opts?.modalSize : ''}`} style={modalStyle} ref={modalRef}>
          {!opts?.hideCloseButton && (
            <IconButton className="ZnglyModal-close-button" aria-label="close" size="large" onClick={handleClose}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          )}
          {title && (
            <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 1 }}>
              {title}
            </Typography>
          )}
          {description && (
            <Typography id="modal-modal-description" sx={{ mt: 1, mb: 1 }}>
              {description}
            </Typography>
          )}

          <Body />
        </Box>
      </Modal>
    </>
  );
};

export default ModalButton;
